// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Group, Loader, Pagination, Stack, Table, Text } from '@mantine/core';
import { IconFileText } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useState } from 'react';
import type { AppointmentDetails } from '../../hooks/useAppointmentDetails';
import { formatAppointmentDate, formatCopay } from '../../utils/appointment-formatting';
import { AppointmentTimeWithTimezone } from './AppointmentTimeWithTimezone';

const ITEMS_PER_PAGE = 5;

interface AppointmentHistoryTableProps {
  appointments: AppointmentDetails[];
  loading: boolean;
}

function PaymentStatusCell({ details }: { details: AppointmentDetails }): JSX.Element {
  if (details.paymentStatus === 'paid' && details.copay && details.copay > 0) {
    return <Anchor size="sm">Paid - View Receipt</Anchor>;
  }
  return <Text size="sm">n/a</Text>;
}

function DocumentLinksCell({ details }: { details: AppointmentDetails }): JSX.Element {
  if (details.documents.length === 0) {
    return <Text size="sm">None</Text>;
  }

  return (
    <Stack gap={4}>
      {details.documents.map((doc) => (
        <Anchor key={doc.id} size="sm" href={`/documents/${encodeURIComponent(doc.id)}`}>
          <Group gap={4} wrap="nowrap">
            <IconFileText size={14} />
            {doc.title}
          </Group>
        </Anchor>
      ))}
    </Stack>
  );
}

/**
 * Table displaying the patient's past appointments with document links and pagination.
 * @param root0 - Component props
 * @param root0.appointments - Enriched appointment details to display
 * @param root0.loading - Whether appointment data is still loading
 * @returns The appointment history table component
 */
export function AppointmentHistoryTable({ appointments, loading }: AppointmentHistoryTableProps): JSX.Element {
  const [page, setPage] = useState(1);

  if (loading) {
    return (
      <Stack align="center" py="md">
        <Loader size="sm" />
      </Stack>
    );
  }

  if (appointments.length === 0) {
    return (
      <Text c="dimmed" py="md">
        No past appointments.
      </Text>
    );
  }

  const totalPages = Math.ceil(appointments.length / ITEMS_PER_PAGE);
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const pageItems = appointments.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  const showing = Math.min(startIndex + ITEMS_PER_PAGE, appointments.length);

  return (
    <>
      <Table miw={1000}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Type</Table.Th>
            <Table.Th>Date</Table.Th>
            <Table.Th>Time</Table.Th>
            <Table.Th>Amount</Table.Th>
            <Table.Th>Payment Status</Table.Th>
            <Table.Th>Provider</Table.Th>
            <Table.Th>Links</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {pageItems.map((details) => (
            <Table.Tr key={details.appointment.id}>
              <Table.Td>{details.appointmentType}</Table.Td>
              <Table.Td>{formatAppointmentDate(details.appointment.start)}</Table.Td>
              <Table.Td>
                <AppointmentTimeWithTimezone
                  start={details.appointment.start}
                  end={details.appointment.end}
                  textProps={{ size: 'sm' }}
                />
              </Table.Td>
              <Table.Td>{formatCopay(details.copay)}</Table.Td>
              <Table.Td>
                <PaymentStatusCell details={details} />
              </Table.Td>
              <Table.Td>
                <Anchor size="sm">{details.providerName}</Anchor>
              </Table.Td>
              <Table.Td>
                <DocumentLinksCell details={details} />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {totalPages > 1 && (
        <Group justify="space-between" mt="md">
          <Text size="sm" c="dimmed">
            {showing} of {appointments.length}
          </Text>
          <Pagination total={totalPages} value={page} onChange={setPage} />
        </Group>
      )}
    </>
  );
}
